/**
 * Central quiz URL mapping module.
 *
 * Edge-runtime compatible - no `fs`, no `server-only`.
 * Used by routes, middleware, and client components to convert between
 * quiz IDs and the new `/quizzes/{subject}/{slug}` URL structure.
 */

export type QuizSubjectConfig = {
  /** URL segment for the subject hub, e.g. "h2-physics" */
  subjectSlug: string;
  /** Internal subject ID used by quiz-index, e.g. "h2-physics-notes" */
  subjectId: string;
  /** Prefix on quiz IDs for this subject, e.g. "h2-physics-" */
  quizIdPrefix: string;
  /** Old hub URL for redirect mapping */
  oldHubPath: string;
  /** Display label on the landing page */
  label: string;
  /** Level grouping: "jc" | "upper-sec" | "lower-sec" */
  level: string;
  /** Label shown above the hub page title */
  subjectLabel: string;
  /** Button text linking to the notes hub */
  notesHubLabel: string;
  /** Name for schema.org ItemList structured data */
  itemListName: string;
  /** Notes hub base path, e.g. "/blog/h2-physics-notes" */
  notesBasePath: string;
  /** SEO page title */
  title: string;
  /** SEO meta description */
  description: string;
};

export const QUIZ_SUBJECT_CONFIGS: QuizSubjectConfig[] = [
  {
    subjectSlug: "h2-chemistry",
    subjectId: "h2-chemistry-notes",
    quizIdPrefix: "h2-chemistry-",
    oldHubPath: "/blog/h2-chemistry-quizzes",
    label: "H2 Chemistry",
    level: "jc",
    subjectLabel: "H2 Chemistry Practice",
    notesHubLabel: "Browse the H2 Chemistry notes hub",
    itemListName: "H2 Chemistry MCQ Practice Topics",
    notesBasePath: "/blog/h2-chemistry-notes",
    title: "H2 Chemistry MCQ Practice Questions - Free Topic-by-Topic",
    description:
      "Free H2 Chemistry MCQ practice questions for A-Level revision. Drill atomic structure, bonding, energetics, organic chemistry, and Paper 1 concepts by topic.",
  },
  {
    subjectSlug: "h2-biology",
    subjectId: "h2-biology-notes",
    quizIdPrefix: "h2-biology-",
    oldHubPath: "/blog/h2-biology-quizzes",
    label: "H2 Biology",
    level: "jc",
    subjectLabel: "H2 Biology Practice",
    notesHubLabel: "Browse the H2 Biology notes hub",
    itemListName: "H2 Biology MCQ Practice Topics",
    notesBasePath: "/blog/h2-biology-notes",
    title: "H2 Biology MCQ Practice Questions - Free Topic-by-Topic",
    description:
      "Free H2 Biology MCQ practice questions for A-Level revision. Drill cell biology, genetics, evolution, infectious diseases, and data-based concepts by topic.",
  },
  {
    subjectSlug: "h2-physics",
    subjectId: "h2-physics-notes",
    quizIdPrefix: "h2-physics-",
    oldHubPath: "/blog/h2-physics-quizzes",
    label: "H2 Physics",
    level: "jc",
    subjectLabel: "H2 Physics Practice",
    notesHubLabel: "Browse the H2 Physics notes hub",
    itemListName: "H2 Physics MCQ Practice Topics",
    notesBasePath: "/blog/h2-physics-notes",
    title: "H2 Physics MCQ Practice Questions - Free Topic-by-Topic",
    description:
      "Free H2 Physics MCQ practice questions for A-Level revision. Drill measurement, forces, fields, waves, circuits, quantum physics, and formula use by topic.",
  },
  {
    subjectSlug: "h2-maths",
    subjectId: "h2-maths-notes",
    quizIdPrefix: "h2-maths-",
    oldHubPath: "/blog/h2-maths-quizzes",
    label: "H2 Maths",
    level: "jc",
    subjectLabel: "H2 Maths Practice",
    notesHubLabel: "Browse the H2 Maths notes hub",
    itemListName: "H2 Maths Concept Check Topics",
    notesBasePath: "/blog/h2-maths-notes",
    title: "H2 Maths Practice Questions - Free Topic-by-Topic Checks",
    description:
      "Free H2 Maths practice questions and concept checks for A-Level revision. Drill functions, calculus, vectors, complex numbers, and statistics by topic.",
  },
  {
    subjectSlug: "ip-chemistry-upper-sec",
    subjectId: "ip-chemistry-upper-sec-notes",
    quizIdPrefix: "ip-chemistry-",
    oldHubPath: "/blog/ip-chemistry-upper-sec-quizzes",
    label: "IP Chemistry",
    level: "upper-sec",
    subjectLabel: "Sec 3-4 Chemistry Practice",
    notesHubLabel: "Browse the Sec 3-4 Chemistry notes hub",
    itemListName: "Sec 3-4 Chemistry MCQ Practice Topics",
    notesBasePath: "/blog/ip-chemistry-upper-sec-notes",
    title: "Sec 3-4 Chemistry MCQ Practice Questions - Free Topic-by-Topic",
    description:
      "Free Sec 3-4 Chemistry MCQ practice questions for IP and O-Level revision. Drill mole calculations, bonding, acids, QA, organic chemistry, and rates by topic.",
  },
  {
    subjectSlug: "ip-biology-upper-sec",
    subjectId: "ip-biology-upper-sec-notes",
    quizIdPrefix: "ip-biology-",
    oldHubPath: "/blog/ip-biology-upper-sec-quizzes",
    label: "IP Biology",
    level: "upper-sec",
    subjectLabel: "Sec 3-4 Biology Practice",
    notesHubLabel: "Browse the Sec 3-4 Biology notes hub",
    itemListName: "Sec 3-4 Biology MCQ Practice Topics",
    notesBasePath: "/blog/ip-biology-upper-sec-notes",
    title: "Sec 3-4 Biology MCQ Practice Questions - Free Topic-by-Topic",
    description:
      "Free Sec 3-4 Biology MCQ practice questions for IP and O-Level revision. Drill cells, transport, nutrition, respiration, inheritance, ecology, and practical ideas.",
  },
  {
    subjectSlug: "ip-physics-upper-sec",
    subjectId: "ip-physics-upper-sec-notes",
    quizIdPrefix: "ip-physics-",
    oldHubPath: "/blog/ip-physics-upper-sec-quizzes",
    label: "IP Physics",
    level: "upper-sec",
    subjectLabel: "Sec 3-4 Physics Practice",
    notesHubLabel: "Browse the Sec 3-4 Physics notes hub",
    itemListName: "Sec 3-4 Physics MCQ Practice Topics",
    notesBasePath: "/blog/ip-physics-upper-sec-notes",
    title: "Sec 3-4 Physics MCQ Practice Questions - Free Topic-by-Topic",
    description:
      "Free Sec 3-4 Physics MCQ practice questions for IP and O-Level revision. Drill measurement, kinematics, forces, light, waves, electricity, and practical skills.",
  },
  {
    subjectSlug: "ip-amaths-upper-sec",
    subjectId: "ip-amaths-upper-sec-notes",
    quizIdPrefix: "ip-amaths-",
    oldHubPath: "/blog/ip-amaths-upper-sec-quizzes",
    label: "IP A.Maths",
    level: "upper-sec",
    subjectLabel: "A Maths Practice",
    notesHubLabel: "Browse the A Maths notes hub",
    itemListName: "A Maths Concept Check Topics",
    notesBasePath: "/blog/ip-amaths-upper-sec-notes",
    title: "A Maths Practice Questions - Free Topic-by-Topic Checks",
    description:
      "Free A Maths practice questions and concept checks for IP and O-Level revision. Drill logarithms, trigonometry, calculus, linear law, and graphs by topic.",
  },
  {
    subjectSlug: "ip-emaths-upper-sec",
    subjectId: "ip-emaths-upper-sec-notes",
    quizIdPrefix: "ip-emaths-",
    oldHubPath: "/blog/ip-emaths-upper-sec-quizzes",
    label: "IP E.Maths",
    level: "upper-sec",
    subjectLabel: "E Maths Practice",
    notesHubLabel: "Browse the E Maths notes hub",
    itemListName: "E Maths Concept Check Topics",
    notesBasePath: "/blog/ip-emaths-upper-sec-notes",
    title: "E Maths Practice Questions - Free Topic-by-Topic Checks",
    description:
      "Free E Maths practice questions and concept checks for IP and O-Level revision. Drill algebra, functions, geometry, trigonometry, statistics, and probability.",
  },
  {
    subjectSlug: "ip-maths-lower-sec",
    subjectId: "ip-maths-lower-sec-notes",
    quizIdPrefix: "ip-maths-",
    oldHubPath: "/blog/ip-maths-lower-sec-quizzes",
    label: "IP Maths",
    level: "lower-sec",
    subjectLabel: "Lower Sec Maths Practice",
    notesHubLabel: "Browse the Lower Sec Maths notes hub",
    itemListName: "Lower Sec Maths Concept Check Topics",
    notesBasePath: "/blog/ip-maths-lower-sec-notes",
    title: "Lower Sec Maths Practice Questions - Free Topic-by-Topic Checks",
    description:
      "Free Sec 1-2 Maths practice questions and concept checks for IP revision. Drill algebra, ratio, geometry, trigonometry, data, and probability by topic.",
  },
];

// ---------------------------------------------------------------------------
// Lookup maps
// ---------------------------------------------------------------------------

export const SUBJECT_SLUG_TO_CONFIG = new Map(
  QUIZ_SUBJECT_CONFIGS.map((c) => [c.subjectSlug, c] as const)
);

export const SUBJECT_ID_TO_CONFIG = new Map(
  QUIZ_SUBJECT_CONFIGS.map((c) => [c.subjectId, c] as const)
);

// ---------------------------------------------------------------------------
// Prefix table for quizId → subject resolution (ordered longest-first)
// ---------------------------------------------------------------------------

const QUIZ_ID_PREFIX_TABLE = QUIZ_SUBJECT_CONFIGS.map((c): [string, string] => [
  c.quizIdPrefix,
  c.subjectSlug,
]).sort((a, b) => b[0].length - a[0].length);

// ---------------------------------------------------------------------------
// Conversion functions
// ---------------------------------------------------------------------------

/** Derive the subject slug from a full quizId by matching its prefix. */
export function subjectSlugFromQuizId(quizId: string): string | null {
  for (const [prefix, slug] of QUIZ_ID_PREFIX_TABLE) {
    if (quizId.startsWith(prefix)) return slug;
  }
  return null;
}

/** Strip the subject prefix from a quizId to produce the URL slug. */
export function quizIdToSlug(quizId: string, subjectSlug: string): string {
  const config = SUBJECT_SLUG_TO_CONFIG.get(subjectSlug);
  if (!config) return quizId;
  return quizId.startsWith(config.quizIdPrefix)
    ? quizId.slice(config.quizIdPrefix.length)
    : quizId;
}

/** Prepend the subject prefix to a URL slug to reconstruct the full quizId. */
export function slugToQuizId(slug: string, subjectSlug: string): string {
  const config = SUBJECT_SLUG_TO_CONFIG.get(subjectSlug);
  if (!config) return slug;
  return `${config.quizIdPrefix}${slug}`;
}

/** Build the canonical URL path for a quizId. */
export function quizIdToUrl(quizId: string): string {
  const subjectSlug = subjectSlugFromQuizId(quizId);
  if (!subjectSlug) return `/quizzes`;
  const slug = quizIdToSlug(quizId, subjectSlug);
  return `/quizzes/${subjectSlug}/${slug}`;
}
